/*
 * @providesModule PushPermissionController
 *
 */

import ClientPropertyConstants, {PushPromptConfig} from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import GlobalEventEmitter from 'GlobalEventEmitter';
import Preferences from 'Preferences';
import SKZConstants from 'SKZConstants';
import SkillzModule from 'SkillzModule';
import {Alert, EmitterSubscription, Linking, Platform} from 'react-native';
import AWSManager from 'AWSManager';
import {_m} from 'INTL';

export const NotificationPermissionStatus = {
  Authorized: 'StatusAuthorized',
  Denied: 'StatusDenied',
  NotDetermined: 'StatusNotDetermined',
};

export type NotificationPermissionStatusType = keyof typeof NotificationPermissionStatus;

class PushPermissionController {
  Status = {
    Authorized: NotificationPermissionStatus.Authorized,
    Denied: NotificationPermissionStatus.Denied,
    NotDetermined: NotificationPermissionStatus.NotDetermined,
  };
  pushNotifListener: EmitterSubscription | null = null;

  getStatus(completion: (status: NotificationPermissionStatusType | (string & {})) => void) {
    if (Platform.OS == 'ios') {
      SkillzModule.getRemoteNotificationPermission((status: string) => {
        const formattedStatus = status.replace('Status', '') as NotificationPermissionStatusType;
        completion(this.Status[formattedStatus]);
      });
    } else {
      completion(this.Status.Authorized);
    }
  }

  displayHardPrompt(completion?: (status: string) => void) {
    if (Platform.OS == 'ios') {
      Preferences.set({[SKZConstants.SKZ_DEFAULTS_HAS_REQUESTED_PUSH]: true});
      if (completion) {
        this.pushNotifListener = GlobalEventEmitter.addListener(
          'pushNotificationStatusChanged',
          (newStatus: string) => {
            completion(newStatus);
            this.pushNotifListener?.remove();
            this.pushNotifListener = null;
          }
        );
      }
      SkillzModule.registerForRemoteNotifications();
    } else if (completion) {
      completion(this.Status.Authorized);
    }
  }

  displaySettingsPrompt(completion?: (status: string) => void) {
    if (Platform.OS == 'ios') {
      if (completion) {
        this.pushNotifListener = GlobalEventEmitter.addListener(
          'pushNotificationStatusChanged',
          (newStatus: string) => {
            completion(newStatus);
            this.pushNotifListener?.remove();
            this.pushNotifListener = null;
          }
        );
      }
      SkillzModule.setupNotificationsForDeniedPermissions();
    } else if (completion) {
      completion(this.Status.Authorized);
    }
  }

  openSettings() {
    Linking.canOpenURL('app-settings:')
      .then((supported: boolean): void => {
        if (supported) {
          Linking.openURL('app-settings:');
        }
      })
      .catch((err: String): void => console.error('An error occurred', err));
  }

  // Specific Prompt Logic

  shouldShowFirstSecondGamePrompt(shouldShow: (shouldShow: boolean) => void) {
    if (Platform.OS == 'android' || Preferences.get(SKZConstants.SKZ_DEFAULTS_HAS_REQUESTED_PUSH)) {
      shouldShow(false);
      return;
    }
    CurrentUserStore.fetchUserProfile(false)
      .then((userProfile: {total_games_played: number}) => {
        const pushPromptConfig = ClientPropertyConstants.getPushPromptConfig();
        const firstGame = pushPromptConfig === PushPromptConfig.FirstGame;
        const secondGame =
          pushPromptConfig === PushPromptConfig.SecondGame && userProfile.total_games_played >= 2;
        shouldShow(firstGame || secondGame);
      })
      .catch((error: unknown) => {
        console.warn(
          'Error getting user profile, cannot determine whether to show push notification hard prompt',
          error
        );
        shouldShow(false);
      });
  }

  public requestPermission(
    triggerType: PushTriggerType,
    completion?: (prompted: boolean, status?: string) => void
  ) {
    if (Platform.OS !== 'ios') {
      completion?.(false);
      return;
    }

    if (!ClientPropertyConstants.getPushDoublePromptEnabled()) {
      completion?.(false);
      return;
    }

    SkillzModule.getRemoteNotificationPermission((status: string) => {
      if (status === this.Status.Authorized) {
        completion?.(false, status);
        return;
      }

      const currentTimeMs = Date.now();

      const timeBetweenPromptMs =
        (ClientPropertyConstants.getPushSecondsBetweenPrompt() ?? 3600) * 1000;

      const lastPushPromptTimeMS =
        parseInt(Preferences.get(SKZConstants.SKZ_LAST_PUSH_PROMPT_TIME_MS), 10) || 0;

      const timeSinceLastPrompt = currentTimeMs - lastPushPromptTimeMS;

      if (timeSinceLastPrompt < timeBetweenPromptMs) {
        completion?.(false, status);
        return;
      }

      Preferences.set({[SKZConstants.SKZ_LAST_PUSH_PROMPT_TIME_MS]: currentTimeMs});

      const hasRequestedPush = Preferences.get(SKZConstants.SKZ_DEFAULTS_HAS_REQUESTED_PUSH);

      AWSManager.trackEvent('PUSH_SOFT_PROMPT_SHOWN', {type: triggerType});

      const {title, message} = promptMessageTitle[triggerType];

      Alert.alert(title, message, [
        {
          text: _m('Not Now'),
          onPress: () => {
            AWSManager.trackEvent('PUSH_SOFT_PROMPT_DENIED', {type: triggerType});
            completion?.(true, status);
          },
          style: 'cancel',
        },
        {
          text: _m('Allow'),
          onPress: () => {
            AWSManager.trackEvent('PUSH_SOFT_PROMPT_ACCEPTED', {type: triggerType});

            if (hasRequestedPush) {
              this.displaySettingsPrompt((status) => completion?.(true, status));
              return;
            }

            AWSManager.trackEvent('PUSH_HARD_PROMPT_SHOWN', {type: triggerType});

            this.displayHardPrompt((newStatus) => {
              switch (newStatus) {
                case this.Status.Authorized:
                  AWSManager.trackEvent('PUSH_HARD_PROMPT_ACCEPTED', {type: triggerType});
                  break;
                case this.Status.Denied:
                  AWSManager.trackEvent('PUSH_HARD_PROMPT_DENIED', {type: triggerType});
                  break;
              }
              completion?.(true, newStatus);
            });
          },
          style: 'default',
        },
      ]);
    });
  }
}

// add additional types here then update promptMessageTitle to setup additional triggers
type PushTriggerType = 'MatchPending' | 'MatchWon' | 'Leagues';

type PromptMessageTitle = {
  [key in PushTriggerType]: {
    title: string;
    message: string;
  };
};

const promptMessageTitle: PromptMessageTitle = {
  MatchPending: {
    title: _m('Get match updates from Skillz'),
    message: _m('Tap "Allow" to receive instant match updates and results'),
  },
  MatchWon: {
    title: _m('Never miss an epic win'),
    message: _m('Tap "Allow" to get updates on match completions and league changes'),
  },
  Leagues: {
    title: _m('Get the latest on Leagues'),
    message: _m('Tap "Allow" to receive updates on rank changes and rewards'),
  },
};

export default new PushPermissionController();
